// Shared venue-requests domain client.
//
// Owns the two venue-request endpoint families that the venues client (see
// domains/venues.js) deliberately left app-side:
//   - member-facing  `/api/venue-requests/*`         (inbox, approve/decline,
//                                                      event residence requests)
//   - guardian-facing `/api/guardian/venue-requests/*` (review queue + history,
//                                                      approve/decline)
//
// The adapter mirrors the createApiClient method surface:
//   get(path, options), post(path, body, options)
//
// Query params (status/limit) and caching/offline-queue transport stay
// app-side: callers pass `params` through `options`, the client only owns paths.
import {
  readGuardianVenueRequestHistoryResponse,
  readGuardianVenueRequestMutationResponse,
  readGuardianVenueRequestsResponse,
  readVenueRequestInboxResponse,
  readVenueRequestRecordResponse,
  readResidenceVenueRequestResponse,
} from '../venue-request-responses.js';
import {readResidenceVenueRequest, readVenueRequestDecisionRequest, readVenueRequestIdentifier, readGuardianVenueRequestQuery} from '../venue-request-write-requests.js';
export {VenueRequestWriteContractError, readResidenceVenueRequest, readVenueRequestDecisionRequest, readVenueRequestIdentifier, readGuardianVenueRequestQuery} from '../venue-request-write-requests.js';
export {
  readGuardianVenueRequestHistoryResponse,
  readGuardianVenueRequestMutationResponse,
  readGuardianVenueRequestsResponse,
  readVenueRequestRecordResponse,
  readResidenceVenueRequestResponse,
} from '../venue-request-responses.js';

export const VENUE_REQUEST_PATHS = {
  // member-facing
  collection: '/api/venue-requests',
  inbox: '/api/venue-requests/inbox',
  // guardian-facing
  guardianCollection: '/api/guardian/venue-requests',
  guardianRecent: '/api/guardian/venue-requests/recent',
};

const enc = (value) => encodeURIComponent(value);

// Member-facing builders
export const buildVenueRequestApprovePath = (requestId) =>
  `${VENUE_REQUEST_PATHS.collection}/${enc(requestId)}/approve`;
export const buildVenueRequestDeclinePath = (requestId) =>
  `${VENUE_REQUEST_PATHS.collection}/${enc(requestId)}/decline`;
export const buildVenueRequestCancelPath = (requestId) =>
  `${VENUE_REQUEST_PATHS.collection}/${enc(requestId)}/cancel`;
export const buildEventResidenceRequestPath = (eventId) =>
  `${VENUE_REQUEST_PATHS.collection}/events/${enc(eventId)}/residence`;
export const buildSessionResidenceRequestPath = (sessionId) =>
  `${VENUE_REQUEST_PATHS.collection}/sessions/${enc(sessionId)}/residence`;

// Guardian-facing builders
export const buildGuardianVenueRequestApprovePath = (requestId) =>
  `${VENUE_REQUEST_PATHS.guardianCollection}/${enc(requestId)}/approve`;
export const buildGuardianVenueRequestDeclinePath = (requestId) =>
  `${VENUE_REQUEST_PATHS.guardianCollection}/${enc(requestId)}/decline`;

export const createVenueRequestsClient = (adapter) => {
  if (!adapter || typeof adapter !== 'object') {
    throw new TypeError('createVenueRequestsClient requires a transport adapter.');
  }

  return {
    // Member-facing
    async fetchInbox(options = {}) {
      return readVenueRequestInboxResponse(await adapter.get(VENUE_REQUEST_PATHS.inbox, options));
    },
    async approve(requestId, payload, options = {}) {
      return readVenueRequestRecordResponse(await adapter.post(buildVenueRequestApprovePath(readVenueRequestIdentifier(requestId)), readVenueRequestDecisionRequest(payload, 'approve'), options), 'venue request approval', 'approved');
    },
    async decline(requestId, payload, options = {}) {
      return readVenueRequestRecordResponse(await adapter.post(buildVenueRequestDeclinePath(readVenueRequestIdentifier(requestId)), readVenueRequestDecisionRequest(payload, 'decline'), options), 'venue request decline', 'declined');
    },
    async cancel(requestId, options = {}) {
      return readVenueRequestRecordResponse(await adapter.post(buildVenueRequestCancelPath(readVenueRequestIdentifier(requestId)), {}, options), 'venue request cancellation', 'cancelled');
    },
    async requestEventResidence(eventId, payload, options = {}) {
      return readResidenceVenueRequestResponse(await adapter.post(buildEventResidenceRequestPath(readVenueRequestIdentifier(eventId)), readResidenceVenueRequest(payload), options), 'event residence request');
    },
    async requestSessionResidence(sessionId, payload, options = {}) {
      return readResidenceVenueRequestResponse(await adapter.post(buildSessionResidenceRequestPath(readVenueRequestIdentifier(sessionId)), readResidenceVenueRequest(payload), options), 'session residence request');
    },

    // Guardian-facing
    async listGuardianRequests(options = {}) {
      readGuardianVenueRequestQuery(options.params);
      return readGuardianVenueRequestsResponse(await adapter.get(VENUE_REQUEST_PATHS.guardianCollection, options));
    },
    async listGuardianRequestHistory(options = {}) {
      readGuardianVenueRequestQuery(options.params);
      return readGuardianVenueRequestHistoryResponse(await adapter.get(VENUE_REQUEST_PATHS.guardianRecent, options));
    },
    async approveGuardianRequest(requestId, payload, options = {}) {
      return readGuardianVenueRequestMutationResponse(await adapter.post(buildGuardianVenueRequestApprovePath(readVenueRequestIdentifier(requestId)), readVenueRequestDecisionRequest(payload, 'approve', true), options), 'guardian venue request approval', 'approved', requestId);
    },
    async declineGuardianRequest(requestId, payload, options = {}) {
      return readGuardianVenueRequestMutationResponse(await adapter.post(buildGuardianVenueRequestDeclinePath(readVenueRequestIdentifier(requestId)), readVenueRequestDecisionRequest(payload, 'decline', true), options), 'guardian venue request decline', 'declined', requestId);
    },
  };
};
